import React, { FC, Suspense } from 'react'

import Box from '@mui/material/Box'
import { Route, Switch } from 'react-router-dom'

import SideBar from './SideBar/SideBar'
import Tasks from './Routes/Tasks/Tasks'
import DashboardError from './DashboardError/DashboardError'
import MainRoutesLazyFallback from './MainRoutesLazyFallback'
import CoachOverview from './Routes/Coaches/CoachOverview/CoachOverview'

import theme from '../../theme'
import {
	PAGE_CLINIC_DASHBOARD,
	PAGE_COACHES,
	PAGE_DASHBOARD,
	PAGE_ORGANIZATION_SETTINGS,
	PAGE_PATIENTS,
	PAGE_PATIENTS_PATIENT_DASHBOARD,
	PAGE_ASSIGN,
	PAGE_USERS,
	PAGE_USER,
	PAGE_ALERTS,
	PAGE_TASKS,
	PAGE_CHAT,
	PAGE_ACTIVITIES,
	PAGE_SEARCH_ORGANIZATION,
	PAGE_UPDATE_ORGANIZATION_GENERAL,
} from '../../constants/navigation'
import {
	selectIsOrgOrSystemAdmin,
	selectIsSysAdmin,
} from '../../store/reducers/user/selectors'
import Error404 from '../../components/Error/Error404'
import { useTypedSelector } from '../../hooks/useTypedSelector'
import { ChatProvider } from '../../hooks/contexts/chat/ChatContext'
import Notifications from '../../components/Notifications/Notifications'

const UserDashboard = React.lazy(
	() =>
		import(
			/* webpackChunkName: "user_dashboard" */ './Routes/UserDashboard/UserDashboard'
		)
)

const PatientOverview = React.lazy(
	() =>
		import(
			/* webpackChunkName: "patient_overview" */ './Routes/PatientOverview/PatientOverview'
		)
)

const Alerts = React.lazy(
	() =>
		import(
			/* webpackChunkName: "alerts_page" */ './Routes/AlertsPage/AlertsPage'
		)
)

const ClinicDashboard = React.lazy(
	() =>
		import(
			/* webpackChunkName: "clinic_dashboard" */ './Routes/ClinicDashboard/ClinicDashboard'
		)
)

const OrganizationSettings = React.lazy(
	() =>
		import(
			/* webpackChunkName: "organization_settings" */ './Routes/OrganizationSettings/OrganizationSettings'
		)
)

const ChatPage = React.lazy(
	() =>
		import(/* webpackChunkName: "chat_page" */ './Routes/ChatPage/ChatPage')
)

const Activities = React.lazy(
	() =>
		import(
			/* webpackChunkName: "activities" */ './Routes/Activities/Activities'
		)
)

const Coaches = React.lazy(
	() => import(/* webpackChunkName: "coaches" */ './Routes/Coaches/Coaches')
)

const Users = React.lazy(
	() => import(/* webpackChunkName: "users" */ './Routes/Users/Users')
)

const Assign = React.lazy(
	() => import(/* webpackChunkName: "assign" */ './Routes/Assign/Assign')
)

const MyPatients = React.lazy(
	() =>
		import(
			/* webpackChunkName: "my_patients" */ './Routes/MyPatients/MyPatients'
		)
)
const SearchOrganization = React.lazy(
	() =>
		import(
			/* webpackChunkName: "search_org" */ './Routes/OrganizationSettings/organizationSearch/OrgainizationSearchMain'
		)
)

const DashboardContent: FC = function () {
	const isSysAdmin = useTypedSelector(selectIsSysAdmin)
	const isOrganisationAdmin = useTypedSelector(selectIsOrgOrSystemAdmin)

	return (
		<Box
			sx={{
				display: 'flex',
				background: '#E5E5E5',
			}}
		>
			<SideBar />

			<Box
				component='main'
				sx={{
					backgroundColor: theme =>
						theme.palette.mode === 'light'
							? theme.palette.grey[100]
							: theme.palette.grey[900],
					flexGrow: 1,
					position: 'relative',
					height: '100vh',
					overflow: 'auto',
					'& .MuiContainer-maxWidthLg': {
						maxWidth: '100%',
					},
					'.Mui-checked': {
						svg: {
							color: theme.palette.primary.main,
						},
					},
					'.Mui-focused .MuiOutlinedInput-notchedOutline': {
						borderColor: theme.palette.primary[300],
						borderWidth: '1px !important',
					},
					'.Mui-focused': {
						color: theme.palette.primary.main,
					},
					'.MuiTableCell-root.MuiTableCell-body': {
						height: '60px',
					}, // should be deleted
					'.MuiTableContainer-root': {
						maxHeight: 'calc(100vh - 300px)',
					},
				}}
			>
				<Switch>
					<Route path={PAGE_DASHBOARD} exact={true}>
						<Suspense fallback={<MainRoutesLazyFallback />}>
							<UserDashboard />
						</Suspense>
					</Route>

					<Route path={PAGE_TASKS} exact={true}>
						<Suspense fallback={<MainRoutesLazyFallback />}>
							<Tasks />
						</Suspense>
					</Route>

					<Route path={PAGE_PATIENTS} exact={true}>
						<Suspense fallback={<MainRoutesLazyFallback />}>
							<MyPatients />
						</Suspense>
					</Route>

					{isSysAdmin && (
						<Route path={PAGE_SEARCH_ORGANIZATION} exact={true}>
							<Suspense fallback={<MainRoutesLazyFallback />}>
								<SearchOrganization />
							</Suspense>
						</Route>
					)}

					<Route path={PAGE_ASSIGN} exact={true}>
						<Suspense fallback={<MainRoutesLazyFallback />}>
							<Assign />
						</Suspense>
					</Route>
					<Route path={PAGE_PATIENTS_PATIENT_DASHBOARD}>
						<Suspense fallback={<MainRoutesLazyFallback />}>
							<PatientOverview />
						</Suspense>
					</Route>

					<Route path={PAGE_ALERTS}>
						<Suspense fallback={<MainRoutesLazyFallback />}>
							<Alerts />
						</Suspense>
					</Route>

					<Route path={PAGE_CHAT}>
						<Suspense fallback={<MainRoutesLazyFallback />}>
							<ChatPage />
						</Suspense>
					</Route>

					<Route path={PAGE_ACTIVITIES}>
						<Suspense fallback={<MainRoutesLazyFallback />}>
							<Activities />
						</Suspense>
					</Route>

					<Route path={PAGE_USERS} exact={true}>
						<Suspense fallback={<MainRoutesLazyFallback />}>
							<Users />
						</Suspense>
					</Route>

					<Route path={PAGE_COACHES} exact={true}>
						<Suspense fallback={<MainRoutesLazyFallback />}>
							<Coaches />
						</Suspense>
					</Route>

					<Route path={PAGE_USER}>
						<Suspense fallback={<MainRoutesLazyFallback />}>
							<CoachOverview />
						</Suspense>
					</Route>

					<Route path={PAGE_CLINIC_DASHBOARD}>
						<Suspense fallback={<MainRoutesLazyFallback />}>
							<ClinicDashboard />
						</Suspense>
					</Route>

					{isOrganisationAdmin && (
						<Route path={PAGE_ORGANIZATION_SETTINGS}>
							<Suspense fallback={<MainRoutesLazyFallback />}>
								<OrganizationSettings />
							</Suspense>
						</Route>
					)}

					{isSysAdmin && (
						<Route path={PAGE_UPDATE_ORGANIZATION_GENERAL}>
							<Suspense fallback={<MainRoutesLazyFallback />}>
								<OrganizationSettings />
							</Suspense>
						</Route>
					)}

					<Route>
						<DashboardError>
							<Error404 />
						</DashboardError>
					</Route>
				</Switch>
			</Box>
		</Box>
	)
}

const Dashboard: FC = () => {
	return (
		<ChatProvider>
			<DashboardContent />

			<Notifications />
		</ChatProvider>
	)
}

export default Dashboard
